import React from 'react';
import { FiDivideCircle } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const ArithmeticFoundationsLesson = () => {
  const sections = [
    {
      id: 'number-sense',
      title: 'Number Sense',
      content: {
        introduction:
          'Before GRE shortcuts work, you need comfort with operations, negatives, fractions, decimals, and order of operations. This lesson builds that base slowly.',
        concepts: [
          {
            title: 'Order of Operations',
            prerequisiteNote:
              'You only need addition, subtraction, multiplication, and division.',
            vocabulary: ['operation', 'parentheses', 'exponent', 'expression'],
            explanation:
              'Use PEMDAS: parentheses first, then exponents, then multiplication/division from left to right, then addition/subtraction from left to right.',
            microExample: {
              problem: 'Calculate §3 + 2 × 5§',
              solution: [
                'Multiplication comes before addition.',
                '§2 × 5 = 10§',
                '§3 + 10 = 13§',
              ],
            },
            greTrap:
              'Do not read left to right blindly. §3 + 2 × 5§ is 13, not 25.',
            checkQuestion:
              'What should you do first in §8 - 2(3 + 1)§?',
            example: {
              problem: 'Evaluate §18 - 3^2 + 4(5 - 2)§',
              solution: [
                'Parentheses: §5 - 2 = 3§',
                'Exponent: §3^2 = 9§',
                'Multiplication: §4(3) = 12§',
                'Combine: §18 - 9 + 12 = 21§',
              ],
            },
          },
          {
            title: 'Negative Numbers',
            prerequisiteNote:
              'Think of negatives as movement left on a number line.',
            vocabulary: ['negative', 'positive', 'opposite', 'absolute value'],
            explanation:
              'A negative sign changes direction. Adding a negative is the same as subtracting. Multiplying two negatives gives a positive.',
            microExample: {
              problem: 'Calculate §-4 + 9§',
              solution: ['Start at -4 and move 9 steps right.', 'Result: §5§'],
            },
            greTrap:
              'The most common error is losing the sign when subtracting a negative.',
            checkQuestion: 'What is §7 - (-3)§?',
            example: {
              problem: 'Evaluate §-6 + 2(5 - 9)§',
              solution: [
                'Parentheses: §5 - 9 = -4§',
                'Multiply: §2(-4) = -8§',
                'Add: §-6 + (-8) = -14§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'fractions-decimals',
      title: 'Fractions and Decimals',
      content: {
        introduction:
          'Fractions and decimals appear in percentages, ratios, probability, rates, and geometry. Learn to move between forms without panic.',
        concepts: [
          {
            title: 'Fraction Basics',
            prerequisiteNote:
              'A fraction is part over whole: numerator over denominator.',
            vocabulary: ['numerator', 'denominator', 'simplify', 'reciprocal'],
            explanation:
              'To add fractions, use a common denominator. To multiply fractions, multiply top by top and bottom by bottom. To divide by a fraction, multiply by its reciprocal.',
            microExample: {
              problem: 'Simplify §6/8§',
              solution: [
                'Both 6 and 8 are divisible by 2.',
                '§6/8 = 3/4§',
              ],
            },
            greTrap:
              'For addition, do not add denominators: §1/2 + 1/3 ≠ 2/5§.',
            checkQuestion: 'What common denominator can you use for §1/3 + 1/4§?',
            example: {
              problem: 'Calculate §1/2 + 1/3§',
              solution: [
                'Common denominator: 6',
                '§1/2 = 3/6§ and §1/3 = 2/6§',
                'Add: §3/6 + 2/6 = 5/6§',
              ],
            },
          },
          {
            title: 'Decimal and Percent Conversions',
            prerequisiteNote:
              'Percent means out of 100.',
            vocabulary: ['decimal', 'percent', 'place value'],
            explanation:
              'To turn a decimal into a percent, multiply by 100. To turn a percent into a decimal, divide by 100.',
            microExample: {
              problem: 'Convert §0.25§ to a percent',
              solution: ['§0.25 × 100 = 25§', 'Answer: 25%'],
            },
            greTrap:
              'A 20% increase means multiply by 1.2, not by 0.2.',
            checkQuestion: 'What decimal equals 15%?',
            example: {
              problem: 'Convert §3/8§ to a decimal and percent',
              solution: [
                'Divide: §3 ÷ 8 = 0.375§',
                'Percent: §0.375 × 100 = 37.5%§',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'untimed',
      question: 'Evaluate §12 - 2(3 + 1)§.',
      options: ['4', '8', '10', '16'],
      correct: 0,
      explanation: 'Parentheses first: §3+1=4§. Then §2×4=8§. Finally §12-8=4§.',
    },
    {
      type: 'untimed',
      question: 'What is §1/2 + 1/4§?',
      options: ['§2/6§', '§2/4§', '§3/4§', '§1/8§'],
      correct: 2,
      explanation: 'Use denominator 4: §1/2=2/4§, so §2/4+1/4=3/4§.',
    },
    {
      type: 'untimed',
      question: 'What is §7 - (-3)§?',
      options: ['4', '10', '-10', '-4'],
      correct: 1,
      explanation: 'Subtracting a negative is addition: §7 + 3 = 10§.',
    },
    {
      type: 'untimed',
      question: 'Simplify §8/12§.',
      options: ['§2/3§', '§3/4§', '§4/6§', '§1/2§'],
      correct: 0,
      explanation: 'Divide top and bottom by 4: §8/12 = 2/3§.',
    },
    {
      type: 'untimed',
      question: 'Evaluate §3 + 2 × 5§.',
      options: ['25', '13', '16', '10'],
      correct: 1,
      explanation: 'Multiply first: §2 × 5 = 10§, then §3 + 10 = 13§.',
    },
    {
      type: 'untimed',
      question: 'What is §1/2 ÷ 1/4§?',
      options: ['§1/8§', '§2/6§', '2', '§1/2§'],
      correct: 2,
      explanation: 'Dividing by a fraction means multiply by its reciprocal: §1/2 × 4/1 = 2§.',
    },
    {
      type: 'untimed',
      question: 'Convert 0.2 to a percent.',
      options: ['0.2%', '2%', '20%', '200%'],
      correct: 2,
      explanation: 'Multiply by 100: §0.2 × 100 = 20%§.',
    },
    {
      type: 'timed',
      timeLimit: 80,
      question: 'What is 35% as a decimal?',
      options: ['0.035', '0.35', '3.5', '35'],
      correct: 1,
      explanation: 'Divide by 100: §35% = 35/100 = 0.35§.',
    },
    {
      type: 'timed',
      timeLimit: 80,
      question: 'Evaluate §-6 + 2(4 - 7)§.',
      options: ['-12', '0', '-2', '12'],
      correct: 0,
      explanation: 'Parentheses: §4-7=-3§. Multiply: §2(-3)=-6§. Add: §-6+(-6)=-12§.',
    },
    {
      type: 'timed',
      timeLimit: 75,
      question: 'What common denominator can you use for §1/3 + 1/6§?',
      options: ['3', '6', '9', '18'],
      correct: 1,
      explanation: '6 is a multiple of both 3 and 6, so §1/3=2/6§ and the sum is §3/6=1/2§.',
    },
  ];

  const quickReference = [
    'PEMDAS: parentheses, exponents, multiply/divide, add/subtract',
    'Adding a negative is subtraction',
    'Two negatives multiplied together give a positive',
    'Percent = out of 100',
    'To divide by a fraction, multiply by its reciprocal',
  ];

  return (
    <LessonTemplate
      lessonId="arithmetic-foundations"
      lessonTitle="Arithmetic Foundations"
      lessonIcon={<FiDivideCircle />}
      lessonDescription="Build the arithmetic base needed before GRE Quant formulas and shortcuts"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={{
        title: 'Need help with Quant foundations?',
        spaceName: 'Quant hacks',
        humanCoachTitle: 'Talk to a GRE coach',
        liwazaAITitle: 'Ask AI',
        communityTitle: 'Ask other students',
        showHumanCoach: true,
        showLiwazaAI: true,
        showCommunity: true,
        aifirstmessage: 'I need help with basic arithmetic for GRE Quant',
        aicontext:
          'Review order of operations, negatives, fractions, decimals, and percents for a beginner GRE Quant learner.',
      }}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="arithmetic-foundations" />
      }
    />
  );
};

export default ArithmeticFoundationsLesson;
